import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LicenseProductBadge from "@/v2-backoffice-client/license/product/LicenseProductBadge"
import type { InfoGridProps } from "@/v2-backoffice-client/components/infoGrid"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import { FENERUM_PLAN_BASE_URL } from "@/v2-backoffice-client/constants"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  LICENSE_PLAN_NO_PRODUCT,
  LICENSE_PLAN_STATUS,
} from "@/v2-backoffice-client/license/plan/license.plan.constants"
import { getLicensePlanStatus } from "@/v2-backoffice-client/license/plan/license.plan.utils"
import {
  deriveSeatModel,
  describeSeatModel,
  formatListPrice,
  SEAT_MODEL_KIND,
  SEAT_MODEL_LABEL,
  type SeatModelText,
} from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

// The seat model is read off the whole catalog (a base license's seat line
// is another row), so the panel resolves it once and hands the grid a row
// that carries it, as the board does.
type LicensePlanInfoRow = LicensePlanFragment & {
  SeatModel: SeatModelText
}

const FIELDS = createInfoGridFields<LicensePlanInfoRow>({
  fields: {
    // Status leads: deprecation recolors every decision on the panel (don't
    // upsell, work the Migration tab, offer the successor), and a license
    // that still needs setup explains its blank usage before anything else.
    DeprecatedAt: {
      label: "Status",
      accessor: (data) => {
        const status = getLicensePlanStatus(data)
        return status === LICENSE_PLAN_STATUS.Deprecated
          ? `Deprecated ${formatDateString(data.DeprecatedAt)}`
          : status
      },
      renderValue({ data }) {
        const status = getLicensePlanStatus(data)
        if (status === LICENSE_PLAN_STATUS.Live) {
          return <DotText variant="success-1">{status}</DotText>
        }
        if (status === LICENSE_PLAN_STATUS.NeedsSetup) {
          return (
            <span className="flex items-center gap-1.5">
              <DotText variant="destructive">{status}</DotText>
              <span className="text-muted-foreground">
                no product, so the import skips it
              </span>
            </span>
          )
        }
        return (
          <span className="flex items-center gap-1.5">
            <DotText variant="warning">
              Deprecated {formatDateString(data.DeprecatedAt)}
            </DotText>
            {data.SuccessorPlanName && (
              <span className="text-muted-foreground">
                → {data.SuccessorPlanName}
              </span>
            )}
          </span>
        )
      },
    },
    // The two fields we own come next: a license without a product is the
    // first thing an operator must see, because the Fenerum import derives
    // nothing from it until one is set; without seat semantics its customers
    // get no seat count.
    ProductName: {
      label: "Product",
      accessor: (data) => data.ProductName ?? LICENSE_PLAN_NO_PRODUCT,
      renderValue({ data }) {
        if (!data.ProductName) {
          return (
            <DotText variant="destructive">{LICENSE_PLAN_NO_PRODUCT}</DotText>
          )
        }
        return <LicenseProductBadge productName={data.ProductName} />
      },
    },
    // Fenerum's price and our seat model, side by side: together they are
    // the one line a salesperson quotes ("2 000 NOK/month, 1 seat included,
    // extra seats on Placepoint Bruker at 1 000 NOK/seat/month"). The
    // Revenue tab's "How it's sold" card spells it out with an example.
    ListPrice: {
      label: "List Price",
      accessor: (data) => formatListPrice(data),
      renderValue({ data }) {
        const text = formatListPrice(data)
        if (!text) {
          return <span className="text-muted-foreground">No active term</span>
        }
        return <span className="tabular-nums">{text}</span>
      },
    },
    SeatModel: {
      label: "Seat Model",
      accessor: (data) =>
        data.SeatModel.detail
          ? `${data.SeatModel.label} · ${data.SeatModel.detail}`
          : data.SeatModel.label,
      valueClassName: "truncate",
      renderValue({ data }) {
        const { label, detail } = data.SeatModel
        if (label === SEAT_MODEL_LABEL[SEAT_MODEL_KIND.Undeclared]) {
          return <DotText variant="destructive">{label}</DotText>
        }
        return (
          <span title={detail ? `${label} · ${detail}` : label}>
            {label}
            {detail && (
              <span className="text-muted-foreground"> · {detail}</span>
            )}
          </span>
        )
      },
    },
    // The license's headline number, visible on every tab. The Revenue tab
    // holds the story behind it (rank, share, per-term split).
    Mrr: {
      label: "Monthly Run-Rate",
      accessor: (data) =>
        data.Mrr != null
          ? formatCompactAmount(data.Mrr, data.MrrCurrency ?? undefined)
          : null,
    },
    LiveSeats: {
      label: "Live Seats",
      accessor: "LiveSeats",
    },
    Code: {
      label: "Fenerum Code",
      accessor: "Code",
      defaultHidden: true,
    },
    Description: {
      label: "Description",
      accessor: "Description",
      valueClassName: "truncate",
      defaultHidden: true,
    },
    LicenseCount: {
      label: "Licenses",
      accessor: "LicenseCount",
      defaultHidden: true,
    },
    VatType: {
      label: "VAT",
      accessor: "VatType",
      defaultHidden: true,
    },
    IsSelfServiceEnabled: {
      label: "Fenerum Self-Service",
      accessor: (data) => (data.IsSelfServiceEnabled ? "Yes" : "No"),
      defaultHidden: true,
    },
    ExternalPlanUuid: {
      label: "Fenerum Id",
      accessor: "ExternalPlanUuid",
      to: ({ data }) => `${FENERUM_PLAN_BASE_URL}/${data.ExternalPlanUuid}/`,
      valueClassName: "truncate",
      defaultHidden: true,
    },
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
  },
})

export type LicensePlanViewInfoProps = Omit<
  InfoGridProps<LicensePlanInfoRow>,
  "data" | "fields"
> & {
  plan: LicensePlanFragment | null | undefined
  /** The full catalog (the board query), for the seat model's seat line. */
  plans: LicensePlanFragment[]
}

function LicensePlanViewInfo(props: LicensePlanViewInfoProps) {
  const { plan, plans, className, ...infoGridProps } = props

  const row: LicensePlanInfoRow | null | undefined = plan
    ? { ...plan, SeatModel: describeSeatModel(deriveSeatModel(plan, plans)) }
    : plan

  return (
    <InfoGrid
      id="license-plan-info"
      className={cn("license-plan-view-info", className)}
      data={row}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicensePlanViewInfo
